import { useCallback, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Flex } from '@zillow/constellation';
import { FieldSet, Label, Legend, Radio } from '@zillow/constellation-10';
import { debounce } from '@zg-rentals/zdash';

import { useSelector } from '../../../../selectors/useSelector';

import WizardServerError from '../../../../model/WizardServerError';
import { GET_LISTING_RECS } from '../../../../constants/RecommendationsConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../../components/Wizard/context';
import { useForm } from '../../../../hooks';
import { postingConstraints } from '../../../../utils/formValidation';
import PropertiesActions from '../../../../actions/PropertiesActions';
import PostingActions from '../../../../actions/PostingActions';
import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../../components/Wizard/actions';
import WizardAlert from '../../../../components/WizardAlert';
import TimeConstants from '../../../../constants/TimeConstants';

import { WizardLabeledControl } from '../../../../styles/wizard';

import type { ChangeEvent } from 'react';

type Props = {
  isRelistingFieldset: boolean;
};

const radioControlName = 'hidePhone';

const AllowContactByPhoneFieldSet = ({ isRelistingFieldset = false }: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { reducerWizardDispatch } = useContext(WizardContext);

  const { listingAlias, dateLastActivatedTimestamp, hidePhone } = useSelector(
    (state) => state.properties.currentProperty,
  );
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    inputs,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      hidePhone: hidePhone.toString(),
    },
    postingConstraints,
  );

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: radioControlName,
          // @ts-expect-error
          value: inputs.hidePhone,
        },
      });
    }
  }, [isSaveComplete]);

  const save = (event: ChangeEvent<HTMLInputElement>) => {
    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const { value, name: fieldName } = event.target;

    if (hasFieldError(fieldName, value)) {
      return;
    }

    const dataToSave = {
      fieldName,
      value,
      propertyId: listingAlias,
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    dispatch(PostingActions.property.edit(dataToSave))
      // @ts-expect-error
      .then(() => {
        dispatch(PropertiesActions.updateCurrentProperty({ [fieldName]: value === 'true' }));
        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((message: string) => {
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS), []);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.persist();

    // @ts-expect-error
    handleFormChange(event);
    saveDebounced(event);
  };

  return (
    <div data-test="AllowContactByPhoneFieldSet">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <FieldSet
        legend={<Legend size={isRelistingFieldset ? 'lg' : 'md'}>Allow renters to contact you by phone</Legend>}
      >
        <Flex
          display="flex"
          paddingLeft={{ default: 'xs', lg: isRelistingFieldset ? '0' : 'xs' }}
          flexDirection={{ default: 'column', lg: isRelistingFieldset ? 'row' : 'column' }}
        >
          <WizardLabeledControl
            controlId="yes"
            label={
              <Label>
                Yes&nbsp;
                <strong>(Recommended)</strong>
              </Label>
            }
            control={
              <Radio
                name={radioControlName}
                value="false"
                checked={inputs.hidePhone === 'false'}
                onChange={handleChange}
              />
            }
          />

          <WizardLabeledControl
            controlId="no"
            label={<Label>No</Label>}
            control={
              <Radio
                name={radioControlName}
                value="true"
                checked={inputs.hidePhone === 'true'}
                onChange={handleChange}
              />
            }
          />
        </Flex>
      </FieldSet>
    </div>
  );
};

export default AllowContactByPhoneFieldSet;
